import * as faceLandmarksDetection from '@tensorflow-models/face-landmarks-detection';
import '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import type { FaceDetector, NormalizedLandmarks, Point3D } from '../types';
import { CONFIG } from '../config';

/**
 * TensorFlow.js with MediaPipe Face Mesh Detector
 */
export class TFJSMediaPipeDetector implements FaceDetector {
  name = 'TensorFlow.js/MediaPipe';
  private detector: faceLandmarksDetection.FaceLandmarksDetector | null = null;
  private initialized = false;
  private solutionPath: string;

  constructor(solutionPath?: string) {
    this.solutionPath = solutionPath || CONFIG.mediaPipePath;
  }

  async initialize(): Promise<void> {
    if (this.initialized && this.detector) {
      return;
    }

    try {
      const model = faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
      const detectorConfig: faceLandmarksDetection.MediaPipeFaceMeshMediaPipeModelConfig = {
        runtime: 'mediapipe',
        maxFaces: 1,
        refineLandmarks: true,
        solutionPath: this.solutionPath
      };

      this.detector = await faceLandmarksDetection.createDetector(model, detectorConfig);
      this.initialized = true;
    } catch (error) {
      throw new Error('Failed to initialize TensorFlow.js/MediaPipe: ' + (error as Error).message);
    }
  }

  async detect(video: HTMLVideoElement): Promise<NormalizedLandmarks | null> {
    if (!this.detector || !this.initialized) {
      return null;
    }

    try {
      const faces = await this.detector.estimateFaces(video, {
        flipHorizontal: false
      });

      if (!faces || faces.length === 0) {
        return null;
      }

      const face = faces[0];
      const keypoints = face.keypoints;

      if (!keypoints || keypoints.length === 0) {
        return null;
      }

      const points: Point3D[] = keypoints.map(kp => ({
        x: kp.x,
        y: kp.y,
        z: kp.z || 0
      }));

      const regions = this.extractRegions(points);

      const normalized: NormalizedLandmarks = {
        points,
        regions,
        confidence: 1.0
      };

      return normalized;
    } catch {
      return null;
    }
  }

  private extractRegions(points: Point3D[]): NormalizedLandmarks['regions'] {
    const leftEyeIndices = [
      33, 7, 163, 144, 145, 153, 154, 155, 133,
      173, 157, 158, 159, 160, 161, 246
    ];

    const rightEyeIndices = [
      263, 249, 390, 373, 374, 380, 381, 382, 362,
      398, 384, 385, 386, 387, 388, 466
    ];

    const leftEyebrowIndices = [
      46, 53, 52, 65, 55, 70, 63, 105, 66, 107
    ];

    const rightEyebrowIndices = [
      276, 283, 282, 295, 285, 300, 293, 334, 296, 336
    ];

    const noseIndices = [
      1, 2, 98, 327, 358, 279, 360, 363, 362, 361,
      242, 97, 99, 94, 19, 4, 5, 6, 168, 195,
      197, 236, 3, 45, 51, 281, 275
    ];

    const mouthIndices = [
      61, 146, 91, 181, 84, 17, 314, 405, 321, 375,
      291, 409, 270, 269, 267, 0, 37, 39, 40, 185,
      76, 77, 90, 80, 81, 82, 13, 312, 311, 310,
      415, 308, 324, 318, 402, 317, 14, 87, 178, 88,
      95, 78, 191, 80, 81, 82
    ];

    const faceOvalIndices = [
      10, 338, 297, 332, 284, 251, 389, 356, 454, 323,
      361, 288, 397, 365, 379, 378, 400, 377, 152, 148,
      176, 149, 150, 136, 172, 58, 132, 93, 234, 127,
      162, 21, 54, 103, 67, 109
    ];

    const jawlineIndices = [
      152, 234, 93, 132, 58, 172, 136, 150, 149, 176,
      148, 400, 377, 378, 379, 365, 397, 288, 361, 323,
      454, 356, 389, 251, 284, 332, 297, 338
    ];

    return {
      leftEye: leftEyeIndices.map(i => points[i]).filter(p => p !== undefined),
      rightEye: rightEyeIndices.map(i => points[i]).filter(p => p !== undefined),
      leftEyebrow: leftEyebrowIndices.map(i => points[i]).filter(p => p !== undefined),
      rightEyebrow: rightEyebrowIndices.map(i => points[i]).filter(p => p !== undefined),
      nose: noseIndices.map(i => points[i]).filter(p => p !== undefined),
      mouth: mouthIndices.map(i => points[i]).filter(p => p !== undefined),
      faceOval: faceOvalIndices.map(i => points[i]).filter(p => p !== undefined),
      jawline: jawlineIndices.map(i => points[i]).filter(p => p !== undefined)
    };
  }

  dispose(): void {
    if (this.detector) {
      this.detector.dispose();
      this.detector = null;
    }
    this.initialized = false;
  }
}
